import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  orderBy,
  limit,
  serverTimestamp,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const HISTORY_COLLECTION = 'youtubeHistory';

/**
 * Gets a history item by YouTube URL for a user
 * @param {string} userId - User ID
 * @param {string} youtubeUrl - YouTube video URL
 * @returns {Promise<Object|null>} - History item or null if not found
 */
export const getHistoryItemByUrl = async (userId, youtubeUrl) => {
  if (!userId || !youtubeUrl) {
    return null;
  }

  try {
    const historyQuery = query(
      collection(db, HISTORY_COLLECTION),
      where('userId', '==', userId),
      where('youtubeUrl', '==', youtubeUrl),
      limit(1)
    );

    const querySnapshot = await getDocs(historyQuery);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      // Convert timestamps
      let createdAt = null;
      let lastAnalyzedAt = null;
      
      if (data.createdAt) {
        if (typeof data.createdAt.toMillis === 'function') {
          createdAt = data.createdAt.toMillis();
        } else if (typeof data.createdAt.toDate === 'function') {
          createdAt = data.createdAt.toDate().getTime();
        } else if (data.createdAt instanceof Date) {
          createdAt = data.createdAt.getTime();
        } else if (typeof data.createdAt === 'number') {
          createdAt = data.createdAt;
        }
      }
      
      if (data.lastAnalyzedAt) {
        if (typeof data.lastAnalyzedAt.toMillis === 'function') {
          lastAnalyzedAt = data.lastAnalyzedAt.toMillis();
        } else if (typeof data.lastAnalyzedAt.toDate === 'function') {
          lastAnalyzedAt = data.lastAnalyzedAt.toDate().getTime();
        } else if (data.lastAnalyzedAt instanceof Date) {
          lastAnalyzedAt = data.lastAnalyzedAt.getTime();
        } else if (typeof data.lastAnalyzedAt === 'number') {
          lastAnalyzedAt = data.lastAnalyzedAt;
        }
      }
      
      return {
        id: doc.id,
        ...data,
        createdAt,
        lastAnalyzedAt,
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting history item:', error);
    return null;
  }
};

/**
 * Gets a history item by video ID for a user (prevents duplicates)
 * @param {string} userId - User ID
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<Object|null>} - History item or null if not found
 */
export const getHistoryItemByVideoId = async (userId, videoId) => {
  if (!userId || !videoId) {
    return null;
  }

  try {
    const historyQuery = query(
      collection(db, HISTORY_COLLECTION),
      where('userId', '==', userId),
      where('videoId', '==', videoId),
      limit(1)
    );

    const querySnapshot = await getDocs(historyQuery);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      // Convert timestamps
      let createdAt = null;
      let lastAnalyzedAt = null;
      
      if (data.createdAt) {
        if (typeof data.createdAt.toMillis === 'function') {
          createdAt = data.createdAt.toMillis();
        } else if (typeof data.createdAt.toDate === 'function') {
          createdAt = data.createdAt.toDate().getTime();
        } else if (data.createdAt instanceof Date) {
          createdAt = data.createdAt.getTime();
        } else if (typeof data.createdAt === 'number') {
          createdAt = data.createdAt;
        }
      }
      
      if (data.lastAnalyzedAt) {
        if (typeof data.lastAnalyzedAt.toMillis === 'function') {
          lastAnalyzedAt = data.lastAnalyzedAt.toMillis();
        } else if (typeof data.lastAnalyzedAt.toDate === 'function') {
          lastAnalyzedAt = data.lastAnalyzedAt.toDate().getTime();
        } else if (data.lastAnalyzedAt instanceof Date) {
          lastAnalyzedAt = data.lastAnalyzedAt.getTime();
        } else if (typeof data.lastAnalyzedAt === 'number') {
          lastAnalyzedAt = data.lastAnalyzedAt;
        }
      }
      
      return {
        id: doc.id,
        ...data,
        createdAt,
        lastAnalyzedAt,
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting history item by videoId:', error);
    return null;
  }
};

/**
 * Saves or updates a YouTube video analysis in user's history
 * Prevents duplicates by checking videoId first, then URL
 * @param {string} userId - User ID
 * @param {string} youtubeUrl - YouTube video URL
 * @param {object} videoData - Video metadata (must include videoId)
 * @returns {Promise<{docId: string, isUpdate: boolean}>} - Document ID and whether it was an update
 */
export const saveToHistory = async (userId, youtubeUrl, videoData) => {
  if (!userId || !youtubeUrl) {
    const error = new Error('User ID and YouTube URL are required');
    console.error('saveToHistory validation failed:', { userId: !!userId, youtubeUrl: !!youtubeUrl });
    throw error;
  }

  // Validate videoData
  if (!videoData || typeof videoData !== 'object') {
    const error = new Error('Video data is required');
    console.error('saveToHistory validation failed: videoData is invalid');
    throw error;
  }

  // videoId is required to prevent duplicates
  if (!videoData.videoId) {
    const error = new Error('Video ID is required in videoData to prevent duplicates');
    console.error('saveToHistory validation failed: videoId is missing');
    throw error;
  }

  try {
    console.log('saveToHistory called:', { 
      userId, 
      youtubeUrl,
      videoId: videoData.videoId,
      videoData: {
        videoTitle: videoData.videoTitle,
        channelName: videoData.channelName,
        thumbnailUrl: videoData.thumbnailUrl,
        viewCount: videoData.viewCount,
        likeCount: videoData.likeCount,
        duration: videoData.duration
      }
    });
    
    // Verify Firestore connection
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }
    
    // First, check by videoId (prevents duplicates regardless of URL format)
    let historyQuery = query(
      collection(db, HISTORY_COLLECTION),
      where('userId', '==', userId),
      where('videoId', '==', videoData.videoId),
      limit(1)
    );

    console.log('Querying for existing history entry by videoId...');
    let querySnapshot = await getDocs(historyQuery);
    
    // If not found by videoId, check by URL (for backward compatibility)
    if (querySnapshot.empty) {
      historyQuery = query(
        collection(db, HISTORY_COLLECTION),
        where('userId', '==', userId),
        where('youtubeUrl', '==', youtubeUrl),
        limit(1)
      );
      console.log('Not found by videoId, checking by URL...');
      querySnapshot = await getDocs(historyQuery);
    }

    console.log('Query result:', querySnapshot.empty ? 'No existing entry found' : 'Existing entry found');

    let docId;
    let isUpdate = false;

    if (!querySnapshot.empty) {
      // Update existing document
      isUpdate = true;
      const existingDoc = querySnapshot.docs[0];
      const docRef = doc(db, HISTORY_COLLECTION, existingDoc.id);
      docId = existingDoc.id;
      
      const updateData = {
        lastAnalyzedAt: serverTimestamp(),
        videoId: videoData.videoId, // Ensure videoId is set
        youtubeUrl: youtubeUrl, // Update URL in case it changed format
        videoTitle: videoData.videoTitle || existingDoc.data().videoTitle || '',
        channelName: videoData.channelName || existingDoc.data().channelName || '',
        thumbnailUrl: videoData.thumbnailUrl || existingDoc.data().thumbnailUrl || '',
        viewCount: videoData.viewCount !== undefined ? videoData.viewCount : (existingDoc.data().viewCount || 0),
        likeCount: videoData.likeCount !== undefined ? videoData.likeCount : (existingDoc.data().likeCount || 0),
        duration: videoData.duration || existingDoc.data().duration || null,
        commentCount: videoData.commentCount !== undefined ? videoData.commentCount : (existingDoc.data().commentCount || 0),
        // Preserve customName and folderId if they exist, don't overwrite them
        customName: existingDoc.data().customName || null,
        folderId: existingDoc.data().folderId || null, // Defaults to null (All Videos folder)
      };
      
      console.log('Updating existing document:', docId, 'with data:', updateData);
      await updateDoc(docRef, updateData);
      console.log('Document updated successfully:', docId);
    } else {
      // Create new document
      const newDocData = {
        userId,
        videoId: videoData.videoId, // Store videoId to prevent duplicates
        youtubeUrl,
        videoTitle: videoData.videoTitle || '',
        channelName: videoData.channelName || '',
        thumbnailUrl: videoData.thumbnailUrl || '',
        viewCount: videoData.viewCount || 0,
        likeCount: videoData.likeCount || 0,
        duration: videoData.duration || null,
        commentCount: videoData.commentCount || 0,
        customName: null, // User can set a custom name for this URL
        folderId: null, // Defaults to null (All Videos folder)
        createdAt: serverTimestamp(),
        lastAnalyzedAt: serverTimestamp(),
      };
      
      console.log('Creating new document with data:', newDocData);
      const docRef = await addDoc(collection(db, HISTORY_COLLECTION), newDocData);
      docId = docRef.id;
      console.log('New document created successfully with ID:', docId);
    }

    // Verify the save by reading back the document
    try {
      const { getDoc } = await import('firebase/firestore');
      const verifyRef = doc(db, HISTORY_COLLECTION, docId);
      const verifySnap = await getDoc(verifyRef);
      
      if (verifySnap.exists()) {
        console.log('Ô£à Save verified: Document exists in Firestore', {
          docId,
          isUpdate,
          data: verifySnap.data()
        });
      } else {
        console.error('ÔØî Save verification failed: Document does not exist after save');
        throw new Error('Document was not saved to Firestore');
      }
    } catch (verifyError) {
      console.error('Error verifying save:', verifyError);
      // Don't throw here - the save might have succeeded but verification failed
    }

    return { docId, isUpdate };
  } catch (error) {
    console.error('ÔØî Error saving to history:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack,
      userId,
      youtubeUrl,
      errorName: error.name
    });
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: Check Firestore security rules. Make sure users can write to youtubeHistory collection.');
    } else if (error.code === 'unavailable') {
      throw new Error('Firestore service unavailable. Please check your internet connection and try again.');
    } else if (error.code === 'failed-precondition') {
      throw new Error('Firestore index required. Please create the required index in Firebase Console.');
    } else if (error.message) {
      throw new Error(`Failed to save video: ${error.message}`);
    } else {
      throw new Error(`Failed to save video to history: ${error.code || 'Unknown error'}`);
    }
  }
};

/**
 * Retrieves user's YouTube analysis history
 * @param {string} userId - User ID
 * @param {number} maxResults - Maximum number of results (default: 50)
 * @param {boolean} recent - If true, only return videos analyzed within the time range, sorted by lastAnalyzedAt DESC
 * @param {number} hours - Number of hours to look back (default: 24). Only used when recent=true
 * @returns {Promise<Array>} - Array of history items
 */
export const getUserHistory = async (userId, maxResults = 50, recent = false, hours = 24) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    // First try with orderBy (requires composite index)
    let querySnapshot;
    let useOrderBy = true;
    
    try {
      let historyQuery;
      
      if (recent) {
        // Filter to last N hours
        const hoursAgo = new Date();
        hoursAgo.setHours(hoursAgo.getHours() - hours);
        const hoursAgoDate = Timestamp.fromDate(hoursAgo);
        
        historyQuery = query(
          collection(db, HISTORY_COLLECTION),
          where('userId', '==', userId),
          where('lastAnalyzedAt', '>=', hoursAgoDate),
          orderBy('lastAnalyzedAt', 'desc'),
          limit(maxResults)
        );
      } else {
        historyQuery = query(
          collection(db, HISTORY_COLLECTION),
          where('userId', '==', userId),
          orderBy('lastAnalyzedAt', 'desc'),
          limit(maxResults)
        );
      }
      
      querySnapshot = await getDocs(historyQuery);
      useOrderBy = true; // If we get here, the index exists
    } catch (indexError) {
      // If index doesn't exist, try without orderBy
      if (indexError.code === 'failed-precondition') {
        console.warn('ÔÜá´©Å Composite index not found, fetching without orderBy. Indexes are being created automatically.');
        useOrderBy = false;
        
        let historyQuery;
        if (recent) {
          const hoursAgo = new Date();
          hoursAgo.setHours(hoursAgo.getHours() - hours);
          const hoursAgoDate = Timestamp.fromDate(hoursAgo);
          
          historyQuery = query(
            collection(db, HISTORY_COLLECTION),
            where('userId', '==', userId),
            where('lastAnalyzedAt', '>=', hoursAgoDate),
            limit(maxResults)
          );
        } else {
          historyQuery = query(
            collection(db, HISTORY_COLLECTION),
            where('userId', '==', userId),
            limit(maxResults)
          );
        }
        
        querySnapshot = await getDocs(historyQuery);
      } else {
        throw indexError;
      }
    }

    const history = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Convert Firestore Timestamp to number (milliseconds) for performance
      let createdAt = null;
      let lastAnalyzedAt = null;
      
      try {
        if (data.createdAt) {
          if (typeof data.createdAt.toMillis === 'function') {
            createdAt = data.createdAt.toMillis();
          } else if (typeof data.createdAt.toDate === 'function') {
            createdAt = data.createdAt.toDate().getTime();
          } else if (data.createdAt instanceof Date) {
            createdAt = data.createdAt.getTime();
          } else if (typeof data.createdAt === 'number') {
            createdAt = data.createdAt;
          }
        }
        
        if (data.lastAnalyzedAt) {
          if (typeof data.lastAnalyzedAt.toMillis === 'function') {
            lastAnalyzedAt = data.lastAnalyzedAt.toMillis();
          } else if (typeof data.lastAnalyzedAt.toDate === 'function') {
            lastAnalyzedAt = data.lastAnalyzedAt.toDate().getTime();
          } else if (data.lastAnalyzedAt instanceof Date) {
            lastAnalyzedAt = data.lastAnalyzedAt.getTime();
          } else if (typeof data.lastAnalyzedAt === 'number') {
            lastAnalyzedAt = data.lastAnalyzedAt;
          }
        }
      } catch (dateError) {
        console.warn('Error converting date:', dateError);
      }
      
      history.push({
        id: doc.id,
        userId: data.userId,
        videoId: data.videoId || null, // Video ID for duplicate detection
        youtubeUrl: data.youtubeUrl,
        videoTitle: data.videoTitle || '',
        channelName: data.channelName || '',
        thumbnailUrl: data.thumbnailUrl || '',
        viewCount: data.viewCount || 0,
        likeCount: data.likeCount || 0,
        duration: data.duration || null,
        commentCount: data.commentCount || 0, // Number of comments on the video
        customName: data.customName || null, // Custom name set by user
        folderId: data.folderId || null, // Folder ID (null if no folder = All Videos)
        isFavorite: data.isFavorite || false, // Favorite status
        createdAt, // Store as timestamp (number)
        lastAnalyzedAt, // Store as timestamp (number)
      });
    });

    // Sort in memory if orderBy wasn't used - much faster with timestamps
    if (!useOrderBy && history.length > 0) {
      history.sort((a, b) => (b.lastAnalyzedAt || 0) - (a.lastAnalyzedAt || 0));
    }

    // If recent=true and we couldn't use the where filter, filter manually
    if (recent && !useOrderBy) {
      const hoursAgo = Date.now() - (hours * 60 * 60 * 1000);
      return history.filter(item => (item.lastAnalyzedAt || 0) >= hoursAgo);
    }

    return history;
  } catch (error) {
    console.error('Error fetching history:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    
    // Return empty array instead of throwing to prevent infinite loading
    if (error.code === 'failed-precondition' || error.code === 'unavailable' || error.message?.includes('index')) {
      console.warn('Firestore index required or service unavailable. Please create a composite index in Firebase Console.');
      return [];
    }
    
    // For permission errors, return empty array
    if (error.code === 'permission-denied') {
      console.error('Permission denied. Check Firestore security rules.');
      return [];
    }
    
    // For other errors, still return empty array to prevent infinite loading
    return [];
  }
};

/**
 * Deletes a history entry
 * @param {string} historyId - History document ID
 * @param {string} userId - User ID (for security verification)
 * @returns {Promise<void>}
 */
export const deleteHistoryItem = async (historyId, userId = null) => {
  if (!historyId) {
    throw new Error('History ID is required');
  }

  if (!userId) {
    throw new Error('User ID is required to delete a video');
  }

  try {
    // Verify ownership before deleting
    const historyDocRef = doc(db, HISTORY_COLLECTION, historyId);
    const historyDocSnap = await getDoc(historyDocRef);
    
    if (!historyDocSnap.exists()) {
      throw new Error('Video not found');
    }
    
    const data = historyDocSnap.data();
    if (data.userId !== userId) {
      throw new Error('You do not have permission to delete this video');
    }

    await deleteDoc(historyDocRef);
  } catch (error) {
    console.error('Error deleting history item:', error);
    throw error;
  }
};

/**
 * Moves a history item to a folder (or removes from folder if folderId is null)
 * @param {string} historyId - History document ID
 * @param {string|null} folderId - Folder ID to move to, or null to remove from folder
 * @returns {Promise<void>}
 */
export const moveHistoryItemToFolder = async (historyId, folderId) => {
  if (!historyId) {
    throw new Error('History ID is required');
  }

  try {
    const docRef = doc(db, HISTORY_COLLECTION, historyId);
    await updateDoc(docRef, {
      folderId: folderId || null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error moving history item to folder:', error);
    throw error;
  }
};

/**
 * Renames a history item (sets custom name)
 * @param {string} historyId - History document ID
 * @param {string|null} customName - Custom name to set, or null to remove custom name
 * @returns {Promise<void>}
 */
export const renameHistoryItem = async (historyId, customName) => {
  if (!historyId) {
    throw new Error('History ID is required');
  }

  try {
    const docRef = doc(db, HISTORY_COLLECTION, historyId);
    await updateDoc(docRef, {
      customName: customName?.trim() || null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error renaming history item:', error);
    throw error;
  }
};

/**
 * Toggles favorite status of a history item
 * @param {string} historyId - History document ID
 * @param {boolean} isFavorite - New favorite status
 * @returns {Promise<void>}
 */
export const toggleFavorite = async (historyId, isFavorite) => {
  if (!historyId) {
    throw new Error('History ID is required');
  }

  try {
    const docRef = doc(db, HISTORY_COLLECTION, historyId);
    await updateDoc(docRef, {
      isFavorite: isFavorite || false,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
};
